import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VideoModalComponent } from '../../core/components/video-modal/video-modal.component';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { ImageCarouselComponent } from '../../core/components/image-carousel/image-carousel.component';
import { BackendService } from '../../core/services/backend.service';
import { Project } from '../../core/interfaces/Project';
import { NgxTgModule } from '@worthy-ventures/metaglotta-ngx';
import { preferred_lang } from '../../core/signals/translation.signal';



@Component({
  selector: 'app-home',
  imports: [CommonModule, MenuBarComponent, FooterComponent, ImageCarouselComponent, NgxTgModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  animations: [
    // trigger('fadeImage', [
    //   transition('* => *', [
    //     style({ opacity: 0, transform: 'scale(0.97)' }),
    //     animate(
    //       '600ms cubic-bezier(0.22, 1, 0.36, 1)',
    //       style({ opacity: 1, transform: 'scale(1)' })
    //     )
    //   ])
    // ])
    trigger('fadeImage', [
      transition('* => *', [
        style({ opacity: 0 }),
        animate('800ms ease-out', style({ opacity: 1 })),
      ]),
    ]),
  ]
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tabsSection', { static: true }) tabsSection!: ElementRef;
  // title/subtitle hold translation keys, resolved with | translate in the template
  tabs: any[] = [
    {
      title: 'home_tab_home_title',
      subtitle: 'home_tab_home_subtitle',
      images: ['/assets/images/homepage/home1.webp', 'assets/images/homepage/home2.webp', 'assets/images/homepage/home3.webp', 'assets/images/homepage/home4.webp'],
      color: '#0FB9B1',
      route: 'solution/home',
    },
    {
      title: 'home_tab_hotels_title',
      subtitle: 'home_tab_hotels_subtitle',
      images: ['assets/images/homepage/hotel1.webp', 'assets/images/homepage/hotel2.webp', 'assets/images/homepage/hotel3.webp', 'assets/images/homepage/hotel4.webp'],
      color: '#F368E0',
      route: 'solution/hotel',
    },
    {
      title: 'home_tab_workspaces_title',
      subtitle: 'home_tab_workspaces_subtitle',
      images: ['assets/images/homepage/workspace1.webp', 'assets/images/homepage/workspace2.webp', 'assets/images/homepage/workspace3.webp', 'assets/images/homepage/workspace4.webp'],
      color: '#6C5CE7',
      route: 'solution/workspace',
    }
  ];
  isMobile = window.innerWidth <= 768;
  // Variables for image carousel on tabs section
  activeIndex = 0;
  activeImageIndex = 0;
  private carouselInterval!: ReturnType<typeof setInterval>;
  private observer!: IntersectionObserver;
  private videoObserver!: IntersectionObserver;
  readonly carouselDelay = 5500; // 5.5 seconds
  private sectionVisible = false;

  // Variables for project carousel
  isAtStart = true;
  isAtEnd = false;
  @ViewChild('carouselWrapper', { static: true }) carouselWrapper!: ElementRef;
  carouselItems: Project[] = [];

  constructor(private router: Router, private el: ElementRef, private backendService: BackendService, private route: ActivatedRoute, private modalService: NgbModal) {
    // Project copy is localised server-side, so switching language has to
    // re-fetch it — Metaglotta only swaps the static strings. Reading the signal
    // here is what subscribes this effect to it; it also covers the first load.
    effect(() => {
      preferred_lang();
      this.backendService.getProjects().subscribe(items => {
        this.carouselItems = items.data;
      });
    });
  }

  ngOnInit() {

    this.route.queryParams.subscribe(params => {
      const sectionId = params['scrollTo'];
      if (sectionId) {
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    });
  }

  ngOnDestroy() {
    this.stopImageCarousel();
    if (this.observer) this.observer.disconnect();
    if (this.videoObserver) this.videoObserver.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  get activeTab() {
    return this.tabs[this.activeIndex];
  }

  //Image carousel on tabs section
  startImageCarousel() {
    this.stopImageCarousel();

    const currentTab = this.tabs[this.activeIndex];
    if (!currentTab?.images?.length) return;

    this.carouselInterval = setInterval(() => {
      this.activeImageIndex = (this.activeImageIndex + 1) % currentTab.images.length;
    }, this.carouselDelay);
  }

  stopImageCarousel() {
    if (this.carouselInterval) clearInterval(this.carouselInterval);
  }

  /**
   * Open a "Where we fit" tab and bring it into view.
   *
   * Expanding a tab pushes its subtitle, button and — on mobile — a carousel
   * into the flow, so the title the user just tapped can end up above the
   * viewport or the new content below it. The scroll waits a frame: the extra
   * content is rendered by change detection, and measuring before that lands
   * scrolls to where the element used to be.
   */
  onTabClick(index: number, el?: HTMLElement) {
    this.activeIndex = index;
    this.activeImageIndex = 0; //reset only on tab change
    this.startImageCarousel();

    if (!el) return;
    // Deliberately NOT scrollIntoView: it does nothing at all for these
    // elements — verified against the running app, with both 'nearest' and
    // 'center' leaving scrollY untouched while window.scrollTo works normally.
    // Computing the delta ourselves also lets the fixed menu bar be accounted
    // for, which scrollIntoView cannot do.
    setTimeout(() => {
      const rect = el.getBoundingClientRect();
      const menu = document.querySelector('.menu') as HTMLElement | null;
      const topInset = (menu ? menu.getBoundingClientRect().bottom : 0) + 16;
      const bottomInset = 16;
      const visibleBottom = window.innerHeight - bottomInset;

      // "Nearest" semantics by hand: move only if the tab is actually clipped,
      // and then only as far as needed, so an already-visible tab stays put.
      let delta = 0;
      if (rect.top < topInset) {
        delta = rect.top - topInset;
      } else if (rect.bottom > visibleBottom) {
        // Never push the top of the tab under the menu while chasing its bottom.
        delta = Math.min(rect.bottom - visibleBottom, rect.top - topInset);
      }
      if (delta !== 0) {
        // Instant, not smooth. Smooth programmatic scrolls are silently dropped
        // in this app — measured against the running page, window.scrollTo and
        // scrollBy both land correctly without a behavior and do nothing at all
        // with behavior:'smooth' (and it is not prefers-reduced-motion). That
        // is the same reason scrollIntoView had no effect here.
        window.scrollBy({ top: delta });
      }
    });
  }

  /* ---- swipe to change the carousel image (mobile) -------------------- *
   * Pointer events rather than touch events: the mobile carousel is the only
   * place this markup renders, and pointer covers finger and stylus without a
   * second code path.
   */
  private swipeStartX: number | null = null;
  private swipeStartY: number | null = null;
  /** Below this the gesture is a tap, not a swipe. */
  private readonly swipeThreshold = 40;

  onSwipeStart(event: PointerEvent): void {
    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;
  }

  onSwipeEnd(event: PointerEvent): void {
    if (this.swipeStartX === null || this.swipeStartY === null) return;
    const dx = event.clientX - this.swipeStartX;
    const dy = event.clientY - this.swipeStartY;
    this.swipeStartX = null;
    this.swipeStartY = null;

    // Horizontal intent only. Without this check a diagonal flick while
    // scrolling the page would also flip the image.
    if (Math.abs(dx) < this.swipeThreshold || Math.abs(dx) <= Math.abs(dy)) return;

    // Drag left = next, drag right = previous, wrapping both ways.
    this.stepImage(dx < 0 ? 1 : -1);
  }

  /** Move the carousel by `delta` images and restart the dwell timer, so a
   *  swipe is not cut short by an auto-advance that was already pending. */
  private stepImage(delta: number): void {
    const images = this.tabs[this.activeIndex]?.images;
    if (!images?.length) return;
    this.activeImageIndex = (this.activeImageIndex + delta + images.length) % images.length;
    this.startImageCarousel();
  }

  onDotClick(index: number) {
    this.activeImageIndex = index;
    this.startImageCarousel(); // restart the interval timer
  }

  ngAfterViewInit(): void {
    const cards = this.el.nativeElement.querySelectorAll('.card');

    // Cards glow effect
    window.addEventListener('mousemove', (ev: MouseEvent) => {
      cards.forEach((card: HTMLElement) => {
        const blob = card.querySelector('.blob') as HTMLElement;
        const fblob = card.querySelector('.fakeblob') as HTMLElement;
        const rect = fblob.getBoundingClientRect();

        // Animate glow position for each card
        blob.animate(
          [
            {
              transform: `translate(${ev.clientX - rect.left - rect.width / 2}px, ${ev.clientY - rect.top - rect.height / 2
                }px)`
            }
          ],
          { duration: 300, fill: 'forwards' }
        );

        // Calculate cursor distance to the card center to fade the glow naturally
        const dx = ev.clientX - (rect.left + rect.width / 2);
        const dy = ev.clientY - (rect.top + rect.height / 2);
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDist = Math.max(window.innerWidth, window.innerHeight) / 2;

        const opacity = 1 - Math.min(distance / maxDist, 1);
        blob.style.opacity = `${opacity}`;
      });
    });

    //Start image carousel on tab section only if the area is visible
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !this.sectionVisible) {
          this.sectionVisible = true;
          this.startImageCarousel();
        }
      },
      { threshold: 0.3 } // start when 30% of section is visible
    );

    this.observer.observe(this.tabsSection.nativeElement);

    // Play each video only when it enters the viewport.
    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            this.videosInView.add(video);
            this.tryPlay(video);
          } else {
            this.videosInView.delete(video);
            this.playAttempts.delete(video);
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    const videos = this.el.nativeElement.querySelectorAll('.bg-video');
    videos.forEach((video: HTMLVideoElement) => {
      this.observedVideos.push(video);

      // A pause nothing in here asked for — the browser suspending background
      // media, a decoder hiccup — otherwise leaves the element stopped for
      // good, because the observer will not fire again while it stays in view.
      // Verified against the running app: play() resolves and `playing` fires,
      // then a pause arrives with no pause() call behind it.
      video.addEventListener('pause', () => this.schedulePlayRetry(video));

      // Real progress, as opposed to a play() that merely resolved, is what
      // clears the retry budget — so a start that is accepted and immediately
      // undone cannot keep refilling it.
      video.addEventListener('timeupdate', () => this.playAttempts.delete(video));

      this.videoObserver.observe(video);
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /* ---- video playback ---------------------------------------------------- *
   * play() rejects for reasons that have nothing to do with whether the video
   * should be playing: the document being hidden (NotAllowedError), the
   * autoplay policy refusing the start, or a pause() racing a still-pending
   * play() (AbortError). Swallowing that rejection parks the video for good,
   * because the observer will not fire again while the element stays in view
   * — which is why videos would occasionally just stop. Every start therefore
   * records intent and keeps retrying for as long as that intent holds.
   *
   * The autoplay policy only lets these start because they are muted, and
   * muting has to be a *property* to count: a bare `muted` attribute in an
   * Angular template is inert, because the attribute seeds the property for
   * parser-created elements only. Hence [muted]="true" in the template — do
   * not turn it back into a plain attribute.
   */

  /** Videos that should be playing right now, so playback can be re-driven
   *  after an event that silently killed it. */
  private readonly videosInView = new Set<HTMLVideoElement>();

  /** Retries already spent on a video, cleared once it actually makes progress
   *  or leaves view, so a start being refused outright backs off rather than
   *  spinning. */
  private readonly playAttempts = new WeakMap<HTMLVideoElement, number>();

  private static readonly MAX_PLAY_RETRIES = 5;

  private tryPlay(video: HTMLVideoElement): void {
    // Pointless while hidden — it would only reject. onVisibilityChange picks
    // these up when the tab comes back.
    if (document.visibilityState !== 'visible') return;

    video.play().catch(() => this.schedulePlayRetry(video));
  }

  /** A refusal is not the last word: the data may not have arrived yet, or a
   *  pause() may have raced the play(). Retry while the video is still meant
   *  to be playing, backing off so a standing refusal cannot spin. */
  private schedulePlayRetry(video: HTMLVideoElement): void {
    if (!this.videosInView.has(video)) return;

    const spent = this.playAttempts.get(video) ?? 0;
    if (spent >= HomeComponent.MAX_PLAY_RETRIES) return;
    this.playAttempts.set(video, spent + 1);

    // Which retry depends on why it failed. Waiting on `canplay`
    // unconditionally would be a trap for an already-loaded element: the
    // event has long since fired, so a one-shot listener would never run.
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      setTimeout(() => this.tryPlay(video), 300 * (spent + 1));
    } else {
      video.addEventListener('canplay', () => this.tryPlay(video), { once: true });
    }
  }

  /** Every video handed to the observer, so playback can be re-derived from
   *  scratch rather than from whatever the observer last reported. */
  private readonly observedVideos: HTMLVideoElement[] = [];

  private isOnScreen(el: Element): boolean {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight
      && r.right > 0 && r.left < window.innerWidth;
  }

  /** Returning to the tab does not re-trigger the observer, so nothing would
   *  restart the videos. Geometry is re-checked rather than replaying the
   *  observer's last word: if a video scrolled into view while the document was
   *  hidden, no callback was ever delivered and videosInView is empty even
   *  though the video is on screen. */
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;

    this.observedVideos.forEach(video => {
      if (this.isOnScreen(video)) {
        this.videosInView.add(video);
        this.tryPlay(video);
      }
    });
  };

  /** Unused — the carousel component navigates for itself. Kept in step with
   *  the slug-keyed route so it is not a trap if it is ever wired up. */
  goToProject(projectSlug: string) {
    this.router.navigate(['/projects', projectSlug]);
  }

  goToUrl(page: string) {
    this.router.navigate([`/${page}`]);
  }

  openHowItWorks() {
    const ref = this.modalService.open(VideoModalComponent, {
      centered: true,
      windowClass: 'video-modal-window',
    });
    ref.componentInstance.videoId = 'KRiRu1AHsvI';
    ref.componentInstance.title = 'How it works';
  }

}
